import { useState, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  EuiButtonIcon,
  EuiDatePicker,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiIcon,
  EuiSelect,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@elastic/eui";
import moment from "moment-timezone";
import { COLORS } from "Consts";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { authSelector } from "reduxStore/auth/auth.selectors";
import { NewProfileData } from "../../profileTypes";
import { useProfileStyles } from "../../profileStyles";
import LookupService from "services/LookupService";
import ToggleButtons from "components/Buttons/ToggleButtons";

interface Props {
  profileDescription: NewProfileData;
  handleProfileDetailsUpdate: (updates: Partial<NewProfileData>) => void;
  handleRemoveTimeFrame: any;
  handleAddTimeFrame: any;
  errorMsgs: any;
  showNameError: boolean;
  showDescriptionError: boolean;
  setNameTouched: any;
  setDescriptionTouched: any;
  isSystemProfile?: boolean;
  isEditMode: boolean;
}

const ProfileDetails = ({
  profileDescription,
  handleProfileDetailsUpdate,
  handleRemoveTimeFrame,
  handleAddTimeFrame,
  errorMsgs,
  showNameError,
  setNameTouched,
  setDescriptionTouched,
  isSystemProfile,
  isEditMode,
}: Props) => {
  const classes = useProfileStyles();
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { clientId } = useSelector(globalFilterSelector, shallowEqual);
  const { allowedClients } = useSelector(authSelector, shallowEqual);

  useEffect(() => {
    setLoading(true);
    LookupService.getTimezones().then((response) => {
      if (response?.data) setTimezones(response?.data);
      setLoading(false);
    });
  }, []);

  const currentClientDetails = allowedClients.find((client) => client.clientID === clientId);
  const isMspClient = currentClientDetails?.isMSP || false;
  const canEditSync = isMspClient && !isEditMode;

  return (
    <>
      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiFormRow
            fullWidth
            label={
              <span style={{ cursor: isSystemProfile ? "default" : "text" }}>
                Profile Name <span style={{ color: "red" }}>*</span>
              </span>
            }
            color="#343741"
            isInvalid={showNameError}
            error={errorMsgs.name}>
            <EuiFieldText
              fullWidth
              placeholder="Enter name"
              value={profileDescription.name}
              onChange={(e) => handleProfileDetailsUpdate({ name: e.target.value })}
              onBlur={() => setNameTouched(true)}
              disabled={isSystemProfile}
              style={{ backgroundColor: "var(--card-background)" }}
            />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="s" />
      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiFormRow
            label={
              <span style={{ cursor: isSystemProfile ? "default" : "text" }}>
                Profile Description
              </span>
            }
            fullWidth>
            <EuiTextArea
              fullWidth
              rows={4}
              placeholder="Enter description"
              value={profileDescription.description}
              onChange={(e) => handleProfileDetailsUpdate({ description: e.target.value })}
              onBlur={() => setDescriptionTouched(true)}
              disabled={isSystemProfile}
              style={{ backgroundColor: "var(--card-background)" }}
            />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="m" />
      <h2 className={classes.formHeading}>Profile Active Times </h2>
      <EuiFlexGroup>
        <EuiFlexItem grow={1}>
          <EuiFormRow
            label={
              <span style={{ cursor: isSystemProfile ? "default" : "text" }}>
                Time Zone <span style={{ color: "red" }}>*</span>
              </span>
            }
            color="#343741"
            fullWidth>
            <EuiSelect
              isLoading={loading}
              fullWidth
              options={timezones}
              value={profileDescription?.activeTimes}
              onChange={(e) => {
                handleProfileDetailsUpdate({ activeTimes: e.target.value });
              }}
              disabled={isSystemProfile}
              style={{ backgroundColor: "var(--card-background)" }}
            />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="m" />
      <div>
        <EuiFormRow
          label={
            <span style={{ cursor: isSystemProfile ? "default" : "text" }}>
              Time Frame <span style={{ color: "red" }}>*</span>
            </span>
          }
          fullWidth>
          <>
            {profileDescription.timeFrames.map((timeFrame: any, index: number) => (
              <EuiFlexGroup
                key={index}
                gutterSize="m"
                alignItems="center"
                style={{ marginBottom: "16px" }}>
                <EuiFlexItem>
                  <EuiSelect
                    style={{ backgroundColor: "var(--card-background)" }}
                    options={[
                      { value: "daily", text: "Always Active" },
                      { value: "weekdays", text: "Weekdays" },
                      { value: "weekends", text: "Weekends" },
                    ]}
                    value={timeFrame.timePeriod}
                    onChange={(e) => {
                      const newTimePeriod = e.target.value;
                      let newTimeFrames;
                      if (newTimePeriod === "daily") {
                        newTimeFrames = [
                          {
                            timePeriod: "daily",
                            startTime: moment("00:00:00", "HH:mm:ss"),
                            endTime: moment("00:00:00", "HH:mm:ss"),
                          },
                        ];
                      } else {
                        const timeFrames = [...profileDescription.timeFrames];
                        timeFrames[index] = {
                          ...timeFrames[index],
                          timePeriod: newTimePeriod,
                        };
                        newTimeFrames = timeFrames;
                      }
                      handleProfileDetailsUpdate({
                        ...profileDescription,
                        timeFrames: newTimeFrames,
                      });
                    }}
                    disabled={isSystemProfile}
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="xs">between</EuiText>
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiDatePicker
                    showTimeSelect
                    showTimeSelectOnly
                    selected={timeFrame.startTime}
                    disabled={timeFrame.timePeriod === "daily" || isSystemProfile}
                    onChange={(date) => {
                      if (date !== null) {
                        const timeFrames = [...profileDescription.timeFrames];
                        timeFrames[index].startTime = date;
                        handleProfileDetailsUpdate({ ...profileDescription, timeFrames });
                      }
                    }}
                    dateFormat="HH:mm A"
                    timeFormat="HH:mm A"
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="xs">and</EuiText>
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiDatePicker
                    showTimeSelect
                    showTimeSelectOnly
                    selected={timeFrame.endTime}
                    disabled={timeFrame.timePeriod === "daily" || isSystemProfile}
                    onChange={(date) => {
                      if (date !== null) {
                        const timeFrames = profileDescription.timeFrames;
                        timeFrames[index].endTime = date;
                        handleProfileDetailsUpdate({ ...profileDescription, timeFrames });
                      }
                    }}
                    dateFormat="HH:mm A"
                    timeFormat="HH:mm A"
                  />
                </EuiFlexItem>
                {profileDescription.timeFrames.length > 1 && (
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon
                      iconType="trash"
                      onClick={() => handleRemoveTimeFrame(index)}
                      aria-label="Remove time frame"
                      size="s"
                      disabled={isSystemProfile}
                    />
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
            ))}
          </>
        </EuiFormRow>
      </div>
      {!profileDescription.timeFrames.some((frame) => frame.timePeriod === "daily") &&
        !isSystemProfile && (
          <>
            <EuiSpacer size="s" />
            <EuiFlexGroup
              gutterSize="none"
              alignItems="center"
              onClick={handleAddTimeFrame}
              style={{ cursor: isSystemProfile ? "not-allowed" : "pointer" }}>
              {" "}
              <EuiFlexItem grow={false}>
                <EuiIcon type="plus" size="s" color={COLORS.$euiColorPrimary} />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs" color={COLORS.$euiColorPrimary}>
                  Add time frame
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        )}

      {canEditSync && !isEditMode && (
        <>
          <EuiSpacer size="s" />
          <h2 className={classes.formHeading}>Apply Profile to all Client Environments</h2>
          <EuiSpacer size="s" />
          <ToggleButtons
            labels={["Yes", "No"]}
            selectedIndex={profileDescription.isSyncedToClients ? 0 : 1}
            disabled={[false, false]}
            onChange={(selectedIndex) => {
              handleProfileDetailsUpdate({ isSyncedToClients: selectedIndex === 0 ? true : false });
            }}
          />
        </>
      )}
    </>
  );
};

export default ProfileDetails;
